import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { View } from "@towersystems/roam-common-native";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Dimensions, Pressable } from "react-native";
import {
  GestureEventPayload,
  PanGestureHandlerEventPayload,
} from "react-native-gesture-handler";
import Animated, {
  useAnimatedScrollHandler,
  useSharedValue,
} from "react-native-reanimated";
import { useHasTransitionEnded } from "../../../utilities/use-after-interactions";
import { DraggableItem } from "../DraggableItem";
import { SortableItem } from "../SortableItem";
import {
  DragHandleProps,
  DragItem,
  ItemTypes,
  ScrollState,
  SortItem,
  SortingStrategy,
} from "../types";
import { useAutoScroll } from "../useAutoScroll";
import { arrayShift, arrayUnchanged } from "../utils";

type Item<T = any> = DragItem<T> | SortItem<T>;

type Props<T> = DragHandleProps & {
  data: Map<string, Item<T>>;
  initialOrder: string[];
  itemWidth: number;
  itemHeight: number;
  numColumns: number;
  itemMargin: number;
  offsetY: number;
  onStart?: () => void;
  onEnd?: () => void;
  onDrop?: (id: string, position: number) => void;
  renderItem: (item: Item<T>, index: number) => JSX.Element;
  onOrderingFinished?: (newOrder: string[]) => void;
};

const DEFAULT_HEIGHT = Dimensions.get("window").height;
const DEFAULT_WIDTH = Dimensions.get("window").width;

const SCROLL_THRESHOLD = 100;
const SCROLL_MS_PER_PX = 2;

export function SortableGrid<T>({
  data,
  initialOrder,
  itemWidth,
  itemHeight,
  numColumns,
  itemMargin,
  offsetY,
  DragHandleContainer,
  DragHandleComponent,
  onStart,
  onEnd,
  renderItem,
  onOrderingFinished = () => {},
  onDrop,
}: Props<T>) {
  const [gridAbsoluteY, setGridAbsoluteY] = useState(0);
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const scrollY = useSharedValue(0);
  const navigation = useNavigation<any>();
  const ready = useHasTransitionEnded(navigation);

  const scrollHandler = useAnimatedScrollHandler((e) => {
    scrollY.value = e.contentOffset.y;
  });

  const sharedOrder = useSharedValue(initialOrder);
  const [prevOrder, setPrevOrder] = useState(sharedOrder.value);
  const [isEditing, setIsEditing] = useState(false);

  const dataArray = [...data.values()];

  const scrollHeight = useMemo(() => {
    const numItems = dataArray.length - 1;
    const rows = Math.floor(numItems / numColumns) + 1;
    const _scrollHeight = itemHeight * rows + 2 * itemMargin * (rows - 1);
    return _scrollHeight;
  }, [height, dataArray.length, numColumns]);

  const { scrollRef, scrollState, scrollToTop, scrollToBottom, cancelScroll } =
    useAutoScroll({
      scrollHeight,
      speed: SCROLL_MS_PER_PX,
    });

  function updateIsEditing(newIsEditingState: boolean) {
    if (newIsEditingState) {
      onStart?.();
    } else {
      onEnd?.();
    }
    setIsEditing(newIsEditingState);
  }

  const cancelEditing = useCallback(() => {
    updateIsEditing(false);
  }, []);

  const hasOrderChanged = useMemo(
    () => !compareScalarArrays(sharedOrder.value, prevOrder),
    [prevOrder, sharedOrder.value]
  );

  function getSortingStrategy(newOrderPosition: number): SortingStrategy {
    const idAtNewPosition = sharedOrder.value[newOrderPosition];

    const shouldIgnore = data.get(idAtNewPosition)?.type === ItemTypes.Sortable;

    if (shouldIgnore) {
      return arrayUnchanged;
    } else {
      return arrayShift;
    }
  }

  function onMoveHandler(
    event: Readonly<GestureEventPayload & PanGestureHandlerEventPayload>
  ) {
    const normalisedY = event.absoluteY - gridAbsoluteY;

    const scrollDown = normalisedY > height - SCROLL_THRESHOLD;
    const scrollUp = normalisedY < SCROLL_THRESHOLD;

    if (scrollDown && scrollState.current !== ScrollState.ScrollingDown) {
      // trigger animate scroll to scrollHeight
      scrollToBottom();
    } else if (scrollUp && scrollState.current !== ScrollState.ScrollingUp) {
      // trigger animate scroll to 0
      scrollToTop();
    } else if (scrollState.current !== ScrollState.NotScrolling) {
      cancelScroll();
    }
  }

  useEffect(() => {
    if (!ready) return;
    scrollRef.current?.measure((...args: number[]) => {
      const pageY = args[5];
      setGridAbsoluteY(pageY);
    });
  }, [ready]);

  useEffect(() => {
    if (!isEditing) {
      cancelScroll();
    }
  }, [isEditing]);

  useFocusEffect(
    useCallback(() => {
      onOrderingFinished(sharedOrder.value);
    }, [])
  );

  useEffect(() => {
    if (hasOrderChanged) {
      setPrevOrder(sharedOrder.value);

      onOrderingFinished(sharedOrder.value);
    }
  }, [hasOrderChanged, onOrderingFinished, sharedOrder.value]);

  const reactiveElementsMarkup = dataArray.map((item, index) => {
    const sortProps = {
      key: index,
      id: item.id,
      order: sharedOrder,
      itemWidth: itemWidth,
      itemHeight: itemHeight,
      margin: itemMargin,
      numColumns: numColumns,
      offsetX: 0,
      offsetY: offsetY,
    };

    switch (item.type) {
      case ItemTypes.Draggable: {
        return (
          <DraggableItem
            {...sortProps}
            //scrollRef={scrollRef}
            scrollY={scrollY}
            isEditing={isEditing}
            onMove={onMoveHandler}
            onDrop={onDrop}
            setIsEditing={updateIsEditing}
            cancelEditing={cancelEditing}
            DragHandleContainer={DragHandleContainer}
            DragHandleComponent={DragHandleComponent}
            getSortingStrategy={getSortingStrategy}
            renderItem={() => renderItem(item, index)}
          />
        );
      }
      case ItemTypes.Sortable: {
        return (
          <SortableItem
            {...sortProps}
            renderItem={() => renderItem(item, index)}
          />
        );
      }
    }
  });

  return (
    <View
      style={{ flex: 1 }}
      onLayout={(event: any) => {
        setHeight(event.nativeEvent.layout.height || DEFAULT_HEIGHT);
        setWidth(event.nativeEvent.layout.width || DEFAULT_WIDTH);
      }}
    >
      <Animated.ScrollView
        onScroll={scrollHandler}
        scrollEnabled={true}
        contentContainerStyle={{ height: scrollHeight }}
        style={{ flex: 1 }}
        ref={scrollRef}
      >
        <Pressable
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
          }}
          onPress={cancelEditing}
        ></Pressable>
        <>{reactiveElementsMarkup}</>
      </Animated.ScrollView>
    </View>
  );
}

function compareScalarArrays<T>(array1: T[], array2: T[]) {
  if (array1.length !== array2.length) {
    return false;
  }
  for (let i = 0; i < array1.length; i++) {
    if (array1[i] !== array2[i]) {
      return false;
    }
  }
  return true;
}
